const TOUCH_LONG_PRESS_MS = 420;
export const GESTURE_CANCEL_MOVE_PX = 10;
const DEFAULT_IGNORE = 'input, textarea, select, button, a[href], option, label';
const SETTLE_MS = 280;
const PAGE_LOCK_CLASS = 'drag-reorder-page-lock';
const OVERFLOW_ESCAPE_CLASS = 'drag-reorder-overflow-escape';

export const DRAG_REORDER_PENDING_CLASS = 'drag-reorder-pending';
export const DRAG_REORDER_ACTIVE_CLASS = 'drag-reorder-active';
export const DRAG_REORDER_SETTLING_CLASS = 'drag-reorder-settling';
export const DRAG_REORDER_ROOT_ACTIVE_CLASS = 'drag-reorder-root-active';

function clientPointFromEvent(event) {
    if (event.touches && event.touches.length > 0) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    if (event.changedTouches && event.changedTouches.length > 0) {
        return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
    }
    if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
        return { x: event.clientX, y: event.clientY };
    }
    return null;
}

function clearTimer(state) {
    if (state.timerId == null) return;
    clearTimeout(state.timerId);
    state.timerId = null;
}

function clearSettleTimer(state) {
    if (state.settleTimerId == null) return;
    clearTimeout(state.settleTimerId);
    state.settleTimerId = null;
}

function isScrollableOverflow(value) {
    return value === 'auto' || value === 'scroll' || value === 'hidden' || value === 'clip';
}

function isGridClipContainer(node) {
    if (!(node instanceof Element)) return false;
    return node.classList.contains('table-scroll-wrapper')
        || node.classList.contains('data-grid-card')
        || node.classList.contains('data-grid')
        || node.classList.contains('data-grid-scroll-region')
        || node.classList.contains('data-grid-body')
        || node.classList.contains('table-card')
        || node.classList.contains('settings-card')
        || node.classList.contains('settings-reorder-grid-card');
}

function lockScrollAndClip(state, item) {
    unlockScrollAndClip(state);

    const locked = [];
    const stopAt = item.closest('.settings-manager-page, .modal-dialog, .modal-content, main') || document.body;
    let node = item.parentElement;
    while (node && node !== stopAt && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        const clips = isScrollableOverflow(style.overflow)
            || isScrollableOverflow(style.overflowX)
            || isScrollableOverflow(style.overflowY);
        if (clips && isGridClipContainer(node)) {
            locked.push({
                el: node,
                overflow: node.style.overflow,
                overflowX: node.style.overflowX,
                overflowY: node.style.overflowY,
            });
            node.classList.add(OVERFLOW_ESCAPE_CLASS);
            node.style.overflowX = 'visible';
            node.style.overflowY = 'visible';
        }
        node = node.parentElement;
    }

    const scrollY = window.scrollY || window.pageYOffset || 0;
    state.pageScrollY = scrollY;
    document.documentElement.classList.add(PAGE_LOCK_CLASS);
    document.body.classList.add(PAGE_LOCK_CLASS);
    document.body.style.top = `-${scrollY}px`;
    state.lockedOverflows = locked;
}

function unlockScrollAndClip(state) {
    (state.lockedOverflows || []).forEach((entry) => {
        entry.el.classList.remove(OVERFLOW_ESCAPE_CLASS);
        entry.el.style.overflow = entry.overflow;
        entry.el.style.overflowX = entry.overflowX;
        entry.el.style.overflowY = entry.overflowY;
    });
    state.lockedOverflows = [];

    document.documentElement.classList.remove(PAGE_LOCK_CLASS);
    document.body.classList.remove(PAGE_LOCK_CLASS);
    if (document.body.style.top) {
        document.body.style.top = '';
        const y = state.pageScrollY || 0;
        window.scrollTo(0, y);
    }
    state.pageScrollY = 0;
}

function resetPending(state) {
    clearTimer(state);
    if (state.pendingItem) {
        state.pendingItem.classList.remove(DRAG_REORDER_PENDING_CLASS);
    }
    state.pendingItem = null;
    state.startPoint = null;
}

function clearActiveVisuals(item) {
    if (!item) return;
    item.classList.remove(DRAG_REORDER_ACTIVE_CLASS);
}

function releasePointer(state) {
    const item = state.captureItem;
    const pointerId = state.pointerId;
    if (item && pointerId != null && typeof item.releasePointerCapture === 'function') {
        try {
            if (item.hasPointerCapture && item.hasPointerCapture(pointerId)) {
                item.releasePointerCapture(pointerId);
            }
        } catch (_err) {
        }
    }
    state.captureItem = null;
    state.pointerId = null;
}

function unbindDocumentDragListeners(state) {
    if (!state.documentDragBound) return;
    document.removeEventListener('pointermove', state.onDocumentPointerMove, true);
    document.removeEventListener('pointerup', state.onDocumentPointerUp, true);
    document.removeEventListener('pointercancel', state.onDocumentPointerCancel, true);
    document.removeEventListener('touchmove', state.onDocumentTouchMove, { capture: true });
    state.documentDragBound = false;
}

function playSettle(state, item) {
    if (!item) return;
    clearSettleTimer(state);
    item.classList.add(DRAG_REORDER_SETTLING_CLASS);
    state.settleTimerId = window.setTimeout(() => {
        item.classList.remove(DRAG_REORDER_SETTLING_CLASS);
        state.settleTimerId = null;
    }, SETTLE_MS);
}

function endActiveDrag(state, event) {
    const item = state.activeItem;
    if (!item) return;
    clearActiveVisuals(item);
    state.root.classList.remove(DRAG_REORDER_ROOT_ACTIVE_CLASS);
    releasePointer(state);
    unbindDocumentDragListeners(state);
    unlockScrollAndClip(state);
    state.activeItem = null;
    state.suppressClick = true;
    playSettle(state, item);
    if (typeof state.onEnd === 'function') {
        state.onEnd(item, event);
    }
    window.setTimeout(() => {
        state.suppressClick = false;
    }, 0);
}

export function bindPointerDragReorder(options) {
    const root = options && options.root;
    const itemSelector = options && options.itemSelector;
    if (!(root instanceof HTMLElement) || !itemSelector) {
        throw new Error('bindPointerDragReorder: root HTMLElement and itemSelector are required');
    }

    const handleSelector = options.handleSelector || null;
    const ignoreSelector = options.ignoreSelector || DEFAULT_IGNORE;
    const cancelMovePx = options.cancelMovePx == null ? GESTURE_CANCEL_MOVE_PX : options.cancelMovePx;
    const signal = options.signal;
    const onActivate = options.onActivate;
    const onMove = options.onMove;
    const onEnd = options.onEnd;
    const listen = signal ? { signal, passive: false } : { passive: false };
    const clickListen = signal ? { signal, capture: true } : { capture: true };

    const state = {
        root,
        timerId: null,
        settleTimerId: null,
        pendingItem: null,
        activeItem: null,
        startPoint: null,
        suppressClick: false,
        onEnd,
        pointerId: null,
        captureItem: null,
        lockedOverflows: [],
        pageScrollY: 0,
        documentDragBound: false,
        onDocumentPointerMove: null,
        onDocumentPointerUp: null,
        onDocumentPointerCancel: null,
        onDocumentTouchMove: null,
    };

    function resolveItem(target) {
        if (!(target instanceof Element)) return null;
        if (target.closest(ignoreSelector)) return null;
        if (handleSelector) {
            const handle = target.closest(handleSelector);
            if (!handle || !root.contains(handle)) return null;
            return handle.closest(itemSelector);
        }
        const item = target.closest(itemSelector);
        if (!item || !root.contains(item)) return null;
        return item;
    }

    function bindDocumentDragListeners() {
        if (state.documentDragBound) return;
        document.addEventListener('pointermove', state.onDocumentPointerMove, true);
        document.addEventListener('pointerup', state.onDocumentPointerUp, true);
        document.addEventListener('pointercancel', state.onDocumentPointerCancel, true);
        document.addEventListener('touchmove', state.onDocumentTouchMove, { capture: true, passive: false });
        state.documentDragBound = true;
    }

    function activate(item, event) {
        clearTimer(state);
        item.classList.remove(DRAG_REORDER_PENDING_CLASS);
        state.pendingItem = null;
        state.activeItem = item;
        root.classList.add(DRAG_REORDER_ROOT_ACTIVE_CLASS);
        item.classList.add(DRAG_REORDER_ACTIVE_CLASS);

        lockScrollAndClip(state, item);
        bindDocumentDragListeners();

        const pointerId = event && event.pointerId;
        if (pointerId != null && typeof item.setPointerCapture === 'function') {
            state.pointerId = pointerId;
            state.captureItem = item;
            try {
                item.setPointerCapture(pointerId);
            } catch (_err) {
            }
        }

        if (typeof onActivate === 'function') {
            onActivate(item);
        }
    }

    state.onDocumentPointerMove = function onDocumentPointerMove(event) {
        if (!state.activeItem) return;
        if (state.pointerId != null && event.pointerId !== state.pointerId) return;
        event.preventDefault();
        const point = clientPointFromEvent(event);
        if (!point) return;
        if (typeof onMove === 'function') {
            onMove(state.activeItem, point, event);
        }
    };

    state.onDocumentPointerUp = function onDocumentPointerUp(event) {
        if (!state.activeItem) return;
        if (state.pointerId != null && event.pointerId !== state.pointerId) return;
        event.preventDefault();
        endActiveDrag(state, event);
    };

    state.onDocumentPointerCancel = function onDocumentPointerCancel(event) {
        if (!state.activeItem) return;
        if (state.pointerId != null && event.pointerId !== state.pointerId) return;
        endActiveDrag(state, event);
    };

    state.onDocumentTouchMove = function onDocumentTouchMove(event) {
        if (!state.activeItem) return;
        event.preventDefault();
    };

    function onPointerDown(event) {
        if (typeof event.button === 'number' && event.button !== 0) return;
        const item = resolveItem(event.target);
        if (!item) return;
        const point = clientPointFromEvent(event);
        if (!point) return;

        resetPending(state);
        item.classList.remove(DRAG_REORDER_SETTLING_CLASS);
        state.pendingItem = item;
        state.startPoint = point;
        state.pointerId = event.pointerId;
        item.classList.add(DRAG_REORDER_PENDING_CLASS);
        const longPressMs = event.pointerType === 'touch' ? TOUCH_LONG_PRESS_MS : 0;
        state.timerId = window.setTimeout(() => {
            if (state.pendingItem !== item) return;
            activate(item, event);
        }, longPressMs);
    }

    function onPointerMove(event) {
        if (state.activeItem) return;
        if (!state.pendingItem || !state.startPoint) return;
        if (state.pointerId != null && event.pointerId !== state.pointerId) return;
        const point = clientPointFromEvent(event);
        if (!point) return;
        const dx = point.x - state.startPoint.x;
        const dy = point.y - state.startPoint.y;
        if ((dx * dx) + (dy * dy) > cancelMovePx * cancelMovePx) {
            resetPending(state);
            state.pointerId = null;
        }
    }

    function onPointerUp(event) {
        if (state.activeItem) return;
        if (state.pointerId != null && event.pointerId !== state.pointerId) return;
        resetPending(state);
        state.pointerId = null;
    }

    function onPointerCancel(event) {
        if (state.activeItem) return;
        resetPending(state);
        state.pointerId = null;
    }

    function onClickCapture(event) {
        if (!state.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        state.suppressClick = false;
    }

    root.addEventListener('pointerdown', onPointerDown, listen);
    root.addEventListener('pointermove', onPointerMove, listen);
    root.addEventListener('pointerup', onPointerUp, listen);
    root.addEventListener('pointercancel', onPointerCancel, listen);
    root.addEventListener('click', onClickCapture, clickListen);

    if (signal) {
        signal.addEventListener('abort', () => {
            resetPending(state);
            clearSettleTimer(state);
            if (state.activeItem) {
                clearActiveVisuals(state.activeItem);
                state.activeItem = null;
            }
            state.root.classList.remove(DRAG_REORDER_ROOT_ACTIVE_CLASS);
            releasePointer(state);
            unbindDocumentDragListeners(state);
            unlockScrollAndClip(state);
        });
    }

    return {
        isActive: () => state.activeItem != null,
        destroy: () => {
            resetPending(state);
            clearSettleTimer(state);
            if (state.activeItem) {
                clearActiveVisuals(state.activeItem);
            }
            state.root.classList.remove(DRAG_REORDER_ROOT_ACTIVE_CLASS);
            releasePointer(state);
            unbindDocumentDragListeners(state);
            unlockScrollAndClip(state);
            state.activeItem = null;
        },
    };
}
